import { Injectable } from '@angular/core';
import { SessionService } from '@monsido/core/session/session.service';
import { ApiService } from '@client/app/services/api/api-service/api.service';
import { BaseRepo } from '@client/app/services/api/base-repo/base-repo';
import { CreateProfileService } from '@client/app/models/page-performance/create-profile/create-profile.service';
import {
    PagePerformanceProfile,
    PageProfileDataInterface,
    PerformanceProfileRepoRequestParams,
} from '@client/app/feature-modules/admin/modules/admin-performance-profiles/types/performance-profile.type';
import { CollectionInterface } from '@monsido/angular-shared-components';
import { HttpContextToken } from '@angular/common/http';
import { DEFAULT_PAGE_SIZE } from '@client/app/shared-modules/api/constants/page-size.constant';
import { DEFAULT_PAGE } from '@client/app/shared-modules/api/constants/page.constant';
import { contextTokens } from '@monsido/services/request-auxiliary/request-auxiliary.service';

@Injectable({
    providedIn: 'root',
})
export class PerformanceProfilesRepo extends BaseRepo {
    constructor (
        private apiService: ApiService,
        protected sessionService: SessionService,
        private createProfileService: CreateProfileService,
    ) {
        super();
    }

    getAll (
        params: PerformanceProfileRepoRequestParams = { page: DEFAULT_PAGE, page_size: DEFAULT_PAGE_SIZE, archived: false },
        contextToken?: HttpContextToken<boolean>,
    ): Promise<CollectionInterface<PagePerformanceProfile>> {
        return this.apiService.get<CollectionInterface<PageProfileDataInterface>>('performance/profiles', params, {}, contextToken || contextTokens.REDESIGN_ERROR_ALERT)
            .then((profilesCollection) => {
                const pageProfiles = profilesCollection.map((data: PageProfileDataInterface) => this.createProfileService.getPagePerformanceProfile(data));
                const { currentPage = 1, perPage = DEFAULT_PAGE_SIZE, total = 0 } = profilesCollection;
                const pageProfilesCollection: CollectionInterface<PagePerformanceProfile> = pageProfiles;
                pageProfilesCollection.currentPage = currentPage;
                pageProfilesCollection.perPage = perPage;
                pageProfilesCollection.total = total;

                return pageProfilesCollection;
            });
    }

    get (id: string): Promise<PagePerformanceProfile> {
        return this.apiService.get<PageProfileDataInterface>('performance/profiles/' + id, undefined, undefined, contextTokens.REDESIGN_ERROR_ALERT)
            .then(data => this.createProfileService.getPagePerformanceProfile(data));
    }

    create (profile: Omit<PagePerformanceProfile, 'id' | 'created_at' | 'updated_at'>): Promise<PagePerformanceProfile> {
        return this.apiService.post<PageProfileDataInterface>('performance/profiles', profile, contextTokens.REDESIGN_ERROR_ALERT)
            .then(data => this.createProfileService.getPagePerformanceProfile(data));
    }

    update (profile: PagePerformanceProfile): Promise<PagePerformanceProfile> {
        return this.apiService.patch<PageProfileDataInterface>('performance/profiles/' + profile.id, profile, undefined, contextTokens.REDESIGN_ERROR_ALERT)
            .then(data => this.createProfileService.getPagePerformanceProfile(data));
    }

    destroy (profile: Pick<PagePerformanceProfile, 'id'>): Promise<void> {
        return this.apiService.delete<void>('performance/profiles/' + profile.id, undefined, contextTokens.REDESIGN_ERROR_ALERT);
    }

    archiveProfile (profile: PageProfileDataInterface): Promise<PagePerformanceProfile> {
        return this.apiService.patch<PageProfileDataInterface>('performance/profiles/' + profile.id + '/archived', { archive: true }, undefined, contextTokens.REDESIGN_ERROR_ALERT)
            .then(() => this.createProfileService.getPagePerformanceProfile(profile));
    }

    unarchiveProfile (profile: PageProfileDataInterface): Promise<PagePerformanceProfile> {
        return this.apiService.patch<PageProfileDataInterface>('performance/profiles/' + profile.id + '/archived', { archive: false }, undefined, contextTokens.REDESIGN_ERROR_ALERT)
            .then(() => this.createProfileService.getPagePerformanceProfile(profile));
    }
}
